/**
 * Quote template data access (#188 follow-on).
 *
 * Mirrors go-service/internal/api/quote_templates.go: per-Company named
 * templates — a name plus two section sets, nine quote-face toggles and
 * seven invoice-face toggles shared by the deposit/balance faces — with at
 * most one optional default. The default swap happens server-side on PUT
 * {is_default: true}; deleting a template still referenced by a Booking is
 * refused with a 409.
 */
import { apiFetch } from "../../../shared/api-client";

/** The quote-face sections a template can toggle. The document identity and
 * the money minimum (totals, amount due) are not sections. A type alias, not
 * an interface: the shared studio shell reads any artifact's section map as
 * `Record<string, boolean>`, and only an alias carries the implicit index
 * signature that allows. */
export type QuoteSections = {
  client_contact: boolean;
  rental_period: boolean;
  validity_note: boolean;
  item_notes: boolean;
  group_totals: boolean;
  totals_breakdown: boolean;
  bank_details: boolean;
  payment_terms: boolean;
  terms_link: boolean;
};

/** The invoice-face sections, shared by deposit and balance invoices. */
export type InvoiceSections = {
  client_contact: boolean;
  rental_period: boolean;
  validity_note: boolean;
  tax_ids: boolean;
  bank_details: boolean;
  payment_terms: boolean;
  terms_link: boolean;
};

export interface QuoteTemplate {
  id: string;
  name: string;
  sections: QuoteSections;
  invoice_sections: InvoiceSections;
  is_default: boolean;
}

export interface QuoteTemplatePayload {
  name: string;
  sections: QuoteSections;
  invoice_sections: InvoiceSections;
  /** PUT only; true promotes this template to the Company default. */
  is_default?: boolean;
}

/** Every quote section on — the floor, and the shape new templates start from. */
export function fullQuoteSections(): QuoteSections {
  return {
    client_contact: true,
    rental_period: true,
    validity_note: true,
    item_notes: true,
    group_totals: true,
    totals_breakdown: true,
    bank_details: true,
    payment_terms: true,
    terms_link: true,
  };
}

/** Every invoice section on — the invoice floor. */
export function fullInvoiceSections(): InvoiceSections {
  return {
    client_contact: true,
    rental_period: true,
    validity_note: true,
    tax_ids: true,
    bank_details: true,
    payment_terms: true,
    terms_link: true,
  };
}

/** List the Company's templates, default first then by name. */
export async function listQuoteTemplates(
  signal?: AbortSignal,
): Promise<QuoteTemplate[]> {
  const res = await apiFetch<QuoteTemplate[]>("/api/admin/quote-templates", {
    signal,
  });
  return res ?? [];
}

/** Create a template. A duplicate name is a 409. */
export async function createQuoteTemplate(
  payload: QuoteTemplatePayload,
): Promise<QuoteTemplate> {
  return apiFetch<QuoteTemplate>("/api/admin/quote-templates", {
    method: "POST",
    body: payload,
  });
}

/** Update name/sections, optionally promoting to default. */
export async function updateQuoteTemplate(
  id: string,
  payload: QuoteTemplatePayload,
): Promise<QuoteTemplate> {
  return apiFetch<QuoteTemplate>(`/api/admin/quote-templates/${id}`, {
    method: "PUT",
    body: payload,
  });
}

/** Delete a template. A Booking-referenced template is a 409. */
export async function deleteQuoteTemplate(id: string): Promise<void> {
  await apiFetch<void>(`/api/admin/quote-templates/${id}`, {
    method: "DELETE",
  });
}
